/* global BX24 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Avatar
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBackIos as PrevIcon,
  ArrowForwardIos as NextIcon,
  NotificationsOutlined as NotificationsIcon,
  EmailOutlined as EmailIcon,
  StarOutline as StarIcon,
  NewReleases as NewReleasesIcon,
  TouchApp as TouchAppIcon
} from '@mui/icons-material';

import '../theme/redesign/tokens.css';
import '../theme/redesign/modal.css';

// Импорт изображений
import analogsImage from '../assets/images/notifications/Список аналогов.png';
import upsellImage from '../assets/images/notifications/Список допродаж.png';
import analogsImprovementsImage from '../assets/images/notifications/Улучшения аналогов.png';
import deliveryAlternativesImage from '../assets/images/notifications/Альтернативные методы доставки.png';
import complaintstransferGoodsImage from '../assets/images/notifications/choice-goods.png';
import complaintsThemesForIncorrectProductsImage from '../assets/images/notifications/themes.png';
import gazobetonRemainderMainBlockImage from '../assets/images/notifications/основной и остаток блоки.png';
import gazobetonAdditionalServicesImage from '../assets/images/notifications/отдельные услуги.png';
import gazobetonUnloadingMethodImage from '../assets/images/notifications/способ разгрузки.png';
import gazobetonCommercialOfferImage from '../assets/images/notifications/полное и неполное кп.png';

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;

// Маппинг полей БД к конфигурации историй (в порядке приоритета показа)
const STORIES_DB_MAPPING = [
  {
    dbField: 'delivery_alternatives_viewed',
    story: {
      id: 'delivery-alternatives',
      title: 'Альтернативные методы доставки',
      duration: 12000, // 12 секунд
      component: 'DeliveryAlternatives'
    }
  },
  {
    dbField: 'analogs_improvements_viewed',
    story: {
      id: 'analogs-improvements',
      title: 'Улучшения в системе аналогов',
      duration: 10000, // 10 секунд
      component: 'AnalogsImprovements'
    }
  },
  {
    dbField: 'story_new_features_viewed',
    story: {
      id: 'new-features',
      title: 'Новые функции калькулятора',
      duration: 8000, // 8 секунд
      component: 'NewFeatures'
    }
  },
  {
    dbField: 'story_bell_info_viewed',
    story: {
      id: 'bell-info',
      title: 'Кнопка уведомлений',
      duration: 5000, // 5 секунд
      component: 'BellInfo'
    }
  },
  {
    dbField: 'complaints_viewed',
    story: {
      id: 'complaints',
      title: 'Кнопка для жалоб и предложений',
      duration: 5000, // 5 секунд
      component: 'Complaints'
    }
  },
  {
    dbField: 'gazobeton_viewed',
    story: {
      id: 'gazobeton_viewed',
      title: 'Газобетон',
      duration: 5000, // 5 секунд
      component: 'GazobetonFirst'
    }
  },
  {
    dbField: 'gazobeton_viewed',
    story: {
      id: 'gazobeton_viewed',
      title: 'Газобетон',
      duration: 5000, // 5 секунд
      component: 'GazobetonSecond'
    }
  },
  {
    dbField: 'gazobeton_viewed',
    story: {
      id: 'gazobeton_viewed',
      title: 'Газобетон',
      duration: 5000, // 5 секунд
      component: 'GazobetonThird'
    }
  }
];

// Все доступные истории для обратной совместимости
const ALL_STORIES_CONFIG = STORIES_DB_MAPPING.map(item => item.story);

const NotificationStories = ({ manualTrigger = false, onClose: externalOnClose, onStoriesViewed, unviewedStories: initialUnviewedStories = [], userName: propUserName, userData = null }) => {
  const [show, setShow] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [unviewedStories, setUnviewedStories] = useState(initialUnviewedStories);
  const [isInitialized, setIsInitialized] = useState(false); // Флаг инициализации

  const intervalRef = useRef(null);
  const dialogRef = useRef(null);
  const imgRef1 = useRef(null);
  const imgRef2 = useRef(null);

  // Функция для определения историй к показу на основе данных пользователя из БД
  const getStoriesToShow = useCallback((userDbData) => {
    if (!userDbData) {
      // Если нет данных пользователя, используем старую логику с unviewedStories
      return ALL_STORIES_CONFIG;
    }

    // Находим непросмотренные истории в порядке приоритета
    const unviewedStories = [];
    const viewedStories = [];

    STORIES_DB_MAPPING.forEach(({ dbField, story }) => {
      if (!userDbData[dbField]) {
        unviewedStories.push(story);
      } else {
        viewedStories.push(story);
      }
    });

    // Показываем сначала непросмотренные, потом просмотренные
    const storiesToShow = [...unviewedStories, ...viewedStories];

    return storiesToShow;
  }, []);

  // Определяем начальный индекс (всегда 0, так как массив уже отсортирован по приоритету)
  const getStartingIndex = useCallback((stories, userDbData) => {
    if (!userDbData) {
      // Старая логика для обратной совместимости
      const firstUnviewedIndex = stories.findIndex(story =>
        initialUnviewedStories.includes(story.id)
      );
      return firstUnviewedIndex !== -1 ? firstUnviewedIndex : 0;
    }

    // Новая логика: первая история в отсортированном массиве - приоритетная
    return 0;
  }, [initialUnviewedStories]);


  // Обновляем локальный state при изменении пропсов
  useEffect(() => {
    // Проверяем изменились ли истории, чтобы избежать бесконечного цикла
    if (JSON.stringify(unviewedStories) !== JSON.stringify(initialUnviewedStories)) {
      setUnviewedStories(initialUnviewedStories);
    }
  }, [initialUnviewedStories, unviewedStories]);

  // Конфигурация историй для показа
  const [storiesConfig, setStoriesConfig] = useState([]);

  // Обновляем конфигурацию историй на основе данных пользователя
  useEffect(() => {
    const stories = getStoriesToShow(userData);
    // Проверяем изменились ли истории, чтобы избежать бесконечного цикла
    if (JSON.stringify(storiesConfig.map(s => s.id)) !== JSON.stringify(stories.map(s => s.id))) {
      setStoriesConfig(stories);
      console.log('📝 Обновлена конфигурация историй:', stories.map(s => s.id));
    }
  }, [userData, getStoriesToShow, storiesConfig]);

  const STORIES_CONFIG = storiesConfig;

  const currentStory = STORIES_CONFIG[currentStoryIndex];
  const totalStories = STORIES_CONFIG.length;

  // Функция для отметки истории как просмотренной
  const markStoryAsViewed = async (storyId) => {
    // Отмечаем историю как просмотренную всегда (и для автоматического, и для ручного просмотра)

    try {
      if (typeof BX24 !== 'undefined' && BX24 !== null) {
        const placementInfo = BX24.placement?.info?.();
        if (placementInfo?.options?.ID) {
          const dealId = placementInfo.options.ID.toString();
          const response = await fetch(`${PATH_TO_BACKEND}/api/deal-assigned-user`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ dealId }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.assignedUserId) {
              await fetch(`${PATH_TO_BACKEND}/api/story-viewed/${data.assignedUserId}/${storyId}`, {
                method: 'POST',
              });
              console.log(`✅ История ${storyId} отмечена как просмотренная`);

              // Обновляем локальный массив непросмотренных историй (для визуальных индикаторов)
              setUnviewedStories(prev => prev.filter(id => id !== storyId));
            }
          }
        }
      }
    } catch (error) {
      console.error('Ошибка при отметке истории как просмотренной:', error);
    }
  };

  // Логика получения данных пользователя (аналогично NewFeatureNotification)
  const fetchUserFromDeal = async (dealId) => {
    if (!dealId) {
      console.warn("⚠️ ID сделки не передан, пропускаем запрос для уведомлений.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${PATH_TO_BACKEND}/api/deal-assigned-user`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ dealId }),
      });

      if (!response.ok) {
        throw new Error("Ошибка при получении ответственного пользователя");
      }

      const data = await response.json();

      if (data.assignedUserId) {
        setUserName(data.assignedUserName || 'Unknown User');
        const userId = data.assignedUserId;
        await checkNotificationStatus(userId);
      } else {
        console.warn("❌ Не удалось получить ID ответственного пользователя.");
        setLoading(false);
      }
    } catch (error) {
      console.error("⚠️ Ошибка при запросе данных пользователя:", error.message);
      setLoading(false);
    }
  };

  const getCurrentUserFromDeal = useCallback(async () => {
    const placementInfo = BX24.placement?.info?.();

    if (placementInfo?.options?.ID) {
      const dealId = placementInfo.options.ID.toString();
      console.log("✅ ID сделки для уведомлений:", dealId);
      await fetchUserFromDeal(dealId);
    } else {
      console.warn("❌ Не удалось получить ID сделки для уведомлений.");
      setLoading(false);
    }
  }, []);

  const checkNotificationStatus = async (userId) => {
    try {
      const response = await fetch(`${PATH_TO_BACKEND}/api/notification-status/${userId}`);

      if (response.ok) {
        const data = await response.json();

        if (data.shouldShowStories && data.unviewedStories.length > 0) {
          // Используем ту же логику определения начального индекса
          setUnviewedStories(data.unviewedStories);
          const firstUnviewedIndex = ALL_STORIES_CONFIG.findIndex(story =>
            data.unviewedStories.includes(story.id)
          );

          const startingIndex = firstUnviewedIndex !== -1 ? firstUnviewedIndex : 0;
          setCurrentStoryIndex(startingIndex);
          setShow(true);
          console.log('✅ Показываем истории для пользователя:', data.user.name, 'Начинаем с индекса:', startingIndex, `(${ALL_STORIES_CONFIG[startingIndex]?.id})`);
        } else {
          console.log('ℹ️ Пользователь уже видел все истории');
        }
      } else {
        console.log('⚠️ Пользователь не найден в базе данных');
      }
      setLoading(false);
    } catch (error) {
      console.error('⚠️ Ошибка при проверке статуса уведомлений:', error);
      setLoading(false);
    }
  };

  // Инициализация (только при первом открытии)
  useEffect(() => {
    if (manualTrigger && !isInitialized) {
      setUserName(propUserName || 'Пользователь');

      // Определяем начальный индекс с помощью новой логики
      const startingIndex = getStartingIndex(STORIES_CONFIG, userData);

      setCurrentStoryIndex(startingIndex);
      console.log(`🚀 Ручной запуск уведомлений. Начинаем с индекса: ${startingIndex}`);
      if (STORIES_CONFIG[startingIndex]) {
        console.log(`📖 Показываем историю: ${STORIES_CONFIG[startingIndex].id}`);
      }

      setShow(true);
      setIsInitialized(true); // Помечаем как инициализированный
      setLoading(false);
      return;
    }

    if (!manualTrigger) {
      // Для автоматического показа теперь отключено - истории показываются только при ручном нажатии
      setLoading(false);
    }
  }, [manualTrigger, propUserName, isInitialized, userData, STORIES_CONFIG, getStartingIndex]);

  // Принудительно устанавливаем стили для изображений в Битриксе
  useEffect(() => {
    if (show && currentStory?.id === 'complaints') {
      // Даем время на рендер
      setTimeout(() => {
        // Используем refs для прямого доступа к изображениям
        [imgRef1, imgRef2].forEach(ref => {
          if (ref.current) {
            const img = ref.current;
            img.style.display = 'block';
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'contain';
            img.style.opacity = '1';
            img.style.visibility = 'visible';
            img.style.maxWidth = 'none';
            img.style.maxHeight = 'none';
            console.log('🖼️ Принудительно устанавливаем стили для изображения:', img.alt);
          }
        });
      }, 100);
    }
  }, [show, currentStory]);

  // Функция закрытия уведомлений
  const handleClose = useCallback(() => {
    // Отмечаем текущую историю как просмотренную при закрытии (без ожидания)
    if (currentStory) {
      markStoryAsViewed(currentStory.id);
    }

    setShow(false);
    setIsInitialized(false); // Сбрасываем флаг инициализации для следующего открытия
    setProgress(0); // Сбрасываем прогресс

    // Уведомляем родительский компонент о просмотре историй
    if (onStoriesViewed) {
      onStoriesViewed();
    }

    if (externalOnClose) {
      externalOnClose();
      return;
    }
  }, [currentStory, onStoriesViewed, externalOnClose, markStoryAsViewed]);

  // Управление прогрессом и автопереключением
  useEffect(() => {
    if (!show || isPaused || !currentStory) return;

    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        const increment = (100 / currentStory.duration) * 50; // обновляем каждые 50мс
        const newProgress = prev + increment;

        if (newProgress >= 100) {
          // Отмечаем текущую историю как просмотренную
          markStoryAsViewed(currentStory.id);

          // Переходим к следующей истории или закрываем
          if (currentStoryIndex < totalStories - 1) {
            setCurrentStoryIndex(prev => prev + 1);
            return 0;
          } else {
            handleClose();
            return 100;
          }
        }

        return newProgress;
      });
    }, 50);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [show, isPaused, currentStoryIndex, currentStory, totalStories, handleClose, markStoryAsViewed]);

  // Управление историями
  const nextStory = () => {
    // Отмечаем текущую историю как просмотренную при переключении (без ожидания)
    if (currentStory) {
      markStoryAsViewed(currentStory.id);
    }

    if (currentStoryIndex < totalStories - 1) {
      setCurrentStoryIndex(prev => prev + 1);
      setProgress(0);
    } else {
      handleClose();
    }
  };

  const prevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
      setProgress(0);
    }
  };

  // Рендер компонента NewFeatures (история 1)
  const renderNewFeaturesStory = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">✨</span>
        <div>
          <h4 className="rd-story__hero-title">
            {userName && `Привет, ${userName}! `}Новые возможности!
          </h4>
          <p className="rd-story__hero-sub">Аналоги для утеплителя и допродажи для товаров</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">Система аналогов и допродаж товаров</h5>
          <span className="rd-story__chip">Новое</span>
        </div>
        <p className="rd-story__text">
          Нажмите на элемент с числом рядом с товаром в блоке «Товары» для просмотра аналогов
          и на элемент с числом в блоке КП для просмотра допродаж.
        </p>
        <ul className="rd-story__list">
          <li><strong>Аналоги</strong> — для утеплителя, ЭППС и OSB</li>
          <li><strong>Допродажи</strong> — для категорий Общестроя</li>
        </ul>

        <div className="rd-story__images">
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: analogsImage, alt: 'Список аналогов' }); }}
          >
            <img src={analogsImage} alt="Список аналогов" />
            <span className="rd-story__image-caption">Аналоги утеплителя</span>
          </button>
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: upsellImage, alt: 'Список допродаж' }); }}
          >
            <img src={upsellImage} alt="Список допродаж" />
            <span className="rd-story__image-caption">Допродажи товаров</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Рендер компонента AnalogsImprovements (история 3)
  const renderAnalogsImprovementsStory = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">🎉</span>
        <div>
          <h4 className="rd-story__hero-title">Улучшения в&nbsp;аналогах!</h4>
          <p className="rd-story__hero-sub">Система аналогов стала удобнее</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">Что нового в&nbsp;списке аналогов</h5>
          <span className="rd-story__chip">Обновлено</span>
        </div>

        <h6 className="rd-story__sub-title">✅ Ваш товар теперь виден в&nbsp;списке</h6>
        <ul className="rd-story__list rd-story__list--muted">
          <li>Зелёная рамка и&nbsp;надпись «ВЫБРАНО»</li>
          <li>Видны все характеристики: производитель, категория, плотность</li>
          <li>Легко сравнивать с&nbsp;аналогами в&nbsp;одном месте</li>
        </ul>

        <h6 className="rd-story__sub-title">💰 Итоговая стоимость</h6>
        <ul className="rd-story__list rd-story__list--muted">
          <li>Сразу видите общую стоимость заказа</li>
          <li>Синяя подчёркнутая цифра — наведите для деталей</li>
          <li>Показывает: цена за&nbsp;упаковку, м² и&nbsp;лист</li>
        </ul>

        <h6 className="rd-story__sub-title">🎯 Упрощённый дизайн</h6>
        <ul className="rd-story__list rd-story__list--muted">
          <li>Убрали лишние столбцы</li>
          <li>Детали цен во&nbsp;всплывающих подсказках</li>
          <li>Яркие цвета для лучшей читаемости</li>
        </ul>

        <div className="rd-story__images">
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: analogsImprovementsImage, alt: 'Улучшения в системе аналогов' }); }}
          >
            <img src={analogsImprovementsImage} alt="Улучшения аналогов" />
            <span className="rd-story__image-caption">Новый вид списка аналогов</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Рендер компонента BellInfo (история 4)
  const renderBellInfoStory = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">🔔</span>
        <div>
          <h4 className="rd-story__hero-title">Кнопка уведомлений</h4>
          <p className="rd-story__hero-sub">Всегда доступна в&nbsp;правом верхнем углу</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">Новая возможность</h5>
          <span className="rd-story__chip">Новое</span>
        </div>
        <p className="rd-story__text">
          В&nbsp;правом верхнем углу появилась кнопка уведомлений. Теперь можно в&nbsp;любое
          время посмотреть информацию о&nbsp;новых функциях калькулятора.
        </p>
        <p className="rd-story__text" style={{ textAlign: 'center' }}>
          🔔&nbsp;<strong>Нажмите на&nbsp;колокольчик</strong> для просмотра обновлений
        </p>
      </div>
    </div>
  );

  // Рендер компонента DeliveryAlternatives (новая история)
  const renderDeliveryAlternativesStory = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">🚚</span>
        <div>
          <h4 className="rd-story__hero-title">Новая функция</h4>
          <p className="rd-story__hero-sub">Альтернативные методы доставки</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">Выбор альтернативного метода доставки</h5>
          <span className="rd-story__chip">Новое</span>
        </div>
        <p className="rd-story__text">
          Теперь в&nbsp;блоке <strong>«КП»</strong> можно выбирать альтернативные методы
          доставки. Сравнивайте варианты и&nbsp;выбирайте наиболее подходящий.
        </p>
        <ul className="rd-story__list">
          <li>Все доступные методы доставки в&nbsp;одной таблице</li>
          <li>Сравнение цен и&nbsp;количества рейсов</li>
          <li>Мгновенное переключение кнопкой «Выбрать»</li>
          <li>Автоматический пересчёт итоговой стоимости</li>
        </ul>

        <div className="rd-story__images">
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: deliveryAlternativesImage, alt: 'Альтернативные методы доставки' }); }}
          >
            <img src={deliveryAlternativesImage} alt="Альтернативные методы доставки" />
            <span className="rd-story__image-caption">Выбор метода доставки в&nbsp;блоке КП</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Рендер компонента renderСomplaintsStory (история 5)
  const renderСomplaintsStory = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">📮</span>
        <div>
          <h4 className="rd-story__hero-title">Жалобы и&nbsp;предложения</h4>
          <p className="rd-story__hero-sub">Всегда доступна в&nbsp;правом верхнем углу</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">Новая возможность</h5>
          <span className="rd-story__chip">Новое</span>
        </div>
        <p className="rd-story__text">
          В&nbsp;правом верхнем углу появилась кнопка жалоб и&nbsp;предложений. Можно
          обратиться к&nbsp;нам, если возникла проблема или появилось предложение
          по&nbsp;улучшению сервиса.
        </p>

        <h6 className="rd-story__sub-title">📍 Передача неверных данных</h6>
        <p className="rd-story__text">
          Также появилась возможность передавать неверные данные. Посмотри, как это работает,
          на&nbsp;скриншотах ниже.
        </p>

        <div className="rd-story__images">
          <button
            type="button"
            className="rd-story__image-card"
            ref={imgRef1}
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: complaintstransferGoodsImage, alt: 'Выбор товара' }); }}
          >
            <img src={complaintstransferGoodsImage} alt="Выбор неверно указанных товаров" />
            <span className="rd-story__image-caption">Выбор неверно указанных товаров</span>
          </button>
          <button
            type="button"
            className="rd-story__image-card"
            ref={imgRef2}
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: complaintsThemesForIncorrectProductsImage, alt: 'Темы с неверными характеристиками' }); }}
          >
            <img src={complaintsThemesForIncorrectProductsImage} alt="Темы передачи неверных товаров" />
            <span className="rd-story__image-caption">Темы передачи неверных товаров</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Рендер компонента renderGazobetonStory first / second / third 
  // Рендер компонента renderGazobetonStoryFirst (история 6)
  const renderGazobetonStoryFirst = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">🧱</span>
        <div>
          <h4 className="rd-story__hero-title">Газобетон (МСК)</h4>
          <p className="rd-story__hero-sub">Что было сделано по&nbsp;газобетону</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">Отдельная логика доставки газобетона&nbsp;МСК</h5>
          <span className="rd-story__chip">Новое</span>
        </div>
        <ul className="rd-story__list">
          <li>Выбор способа разгрузки (манипулятором, краном или без&nbsp;разгрузки)</li>
          <li>Универсальная подсказка для менеджеров — становится доступна при выборе газобетона в&nbsp;качестве товара</li>
          <li>В&nbsp;блоке «Товары» добавлена ячейка «м³ кратно поддонам»</li>
          <li>Заполнение Bitrix: структура приведена к&nbsp;логике КП, одинаковые позиции объединяются, доставка и&nbsp;услуги передаются отдельными корректными строками</li>
        </ul>

        <div className="rd-story__images">
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: gazobetonUnloadingMethodImage, alt: 'Способы разгрузки' }); }}
          >
            <img src={gazobetonUnloadingMethodImage} alt="Способы разгрузки" />
            <span className="rd-story__image-caption">Выбор города и&nbsp;способа разгрузки</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Рендер компонента renderGazobetonStorySecond (история 7)
  const renderGazobetonStorySecond = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">📦</span>
        <div>
          <h4 className="rd-story__hero-title">Газобетон (МСК)</h4>
          <p className="rd-story__hero-sub">Продолжение презентации</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">КП для газобетона визуально переработан</h5>
          <span className="rd-story__chip">Новое</span>
        </div>
        <p className="rd-story__text">Коммерческое предложение разделено на&nbsp;2 блока:</p>
        <ul className="rd-story__list">
          <li>«Основной блок»</li>
          <li>«Остаток и&nbsp;отдельные услуги»</li>
        </ul>

        <div className="rd-story__images">
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: gazobetonRemainderMainBlockImage, alt: 'Основной блок и блок с остатками' }); }}
          >
            <img src={gazobetonRemainderMainBlockImage} alt="Основной блок и блок с остатками" />
            <span className="rd-story__image-caption">Основной блок и&nbsp;блок с&nbsp;остатками: газобетона, поддонов, доп. услуг</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Рендер компонента renderGazobetonStoryThird (история 8)
  const renderGazobetonStoryThird = () => (
    <div className="rd-story">
      <div className="rd-story__hero">
        <span className="rd-story__hero-icon" aria-hidden="true">🛠️</span>
        <div>
          <h4 className="rd-story__hero-title">Газобетон (МСК)</h4>
          <p className="rd-story__hero-sub">Ещё немного о&nbsp;газобетоне</p>
        </div>
      </div>

      <div className="rd-story__card">
        <div className="rd-story__card-head">
          <span className="rd-story__card-icon" aria-hidden="true" />
          <h5 className="rd-story__card-title">Допродажи и&nbsp;дополнительные услуги</h5>
          <span className="rd-story__chip">Новое</span>
        </div>
        <p className="rd-story__text">
          Формирование сообщений стало ещё лучше: чистая структура текста, объединение
          одинаковых позиций, корректная работа для «Полного КП» и&nbsp;«Неполного КП».
        </p>
        <ul className="rd-story__list">
          <li>Допродажи для газобетона: U-блоки, клей, добавки, отдельные услуги</li>
        </ul>

        <div className="rd-story__images">
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: gazobetonAdditionalServicesImage, alt: 'Дополнительные услуги' }); }}
          >
            <img src={gazobetonAdditionalServicesImage} alt="Дополнительные услуги" />
            <span className="rd-story__image-caption">Дополнительные услуги и&nbsp;остатки</span>
          </button>
          <button
            type="button"
            className="rd-story__image-card"
            onClick={(e) => { e.stopPropagation(); setFullscreenImage({ src: gazobetonCommercialOfferImage, alt: 'Полное и неполное КП' }); }}
          >
            <img src={gazobetonCommercialOfferImage} alt="Полное и неполное КП" />
            <span className="rd-story__image-caption">Режимы отправки сообщения: «Полное КП» и&nbsp;«Неполное КП»</span>
          </button>
        </div>
      </div>
    </div>
  );

  if (loading || (!manualTrigger && typeof BX24 === 'undefined')) {
    return null;
  }

  if (!show || totalStories === 0 || !currentStory) {
    return null;
  }

  return ReactDOM.createPortal(
    <>
      {show && (
        <div
          className="rd-modal-overlay"
          onClick={handleClose}
          role="presentation"
        >
          <div
            ref={dialogRef}
            className="rd-modal rd-stories"
            role="dialog"
            aria-modal="true"
            aria-label={currentStory.title}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Segmented progress сверху */}
            <div className="rd-stories__progress">
              {totalStories > 0 && STORIES_CONFIG.map((story, index) => {
                const isViewed = !unviewedStories.includes(story.id);
                const value = index < currentStoryIndex
                  ? 100
                  : (index === currentStoryIndex ? progress : 0);
                return (
                  <div
                    key={index}
                    className={`rd-stories__progress-seg${isViewed ? " rd-stories__progress-seg--viewed" : ""}`}
                  >
                    <span
                      className="rd-stories__progress-bar"
                      style={{ width: `${value}%` }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Шапка: заголовок + просмотрено + 1/8 + ✕ */}
            <div className="rd-stories__head">
              <div className="rd-stories__head-titles">
                <h3 className="rd-stories__title">{currentStory.title}</h3>
                {!unviewedStories.includes(currentStory.id) && (
                  <span className="rd-stories__badge rd-stories__badge--viewed">
                    Просмотрено
                  </span>
                )}
              </div>
              <div className="rd-stories__head-meta">
                <span className="rd-stories__badge rd-stories__badge--counter">
                  {currentStoryIndex + 1}/{totalStories}
                </span>
                <button
                  type="button"
                  className="rd-modal__close rd-stories__close"
                  onClick={handleClose}
                  aria-label="Закрыть"
                  title="Закрыть"
                />
              </div>
            </div>

            {/* Тело: кликабельные половины (prev/next) */}
            <div
              className="rd-stories__body"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const centerX = rect.width / 2;
                if (clickX > centerX) {
                  nextStory();
                } else {
                  prevStory();
                }
              }}
            >
              {currentStory.component === 'DeliveryAlternatives' && renderDeliveryAlternativesStory()}
              {currentStory.component === 'BellInfo' && renderBellInfoStory()}
              {currentStory.component === 'NewFeatures' && renderNewFeaturesStory()}
              {currentStory.component === 'AnalogsImprovements' && renderAnalogsImprovementsStory()}
              {currentStory.component === 'Complaints' && renderСomplaintsStory()}
              {currentStory.component === 'GazobetonFirst' && renderGazobetonStoryFirst()}
              {currentStory.component === 'GazobetonSecond' && renderGazobetonStorySecond()}
              {currentStory.component === 'GazobetonThird' && renderGazobetonStoryThird()}

              {currentStoryIndex > 0 && (
                <button
                  type="button"
                  className="rd-stories__nav rd-stories__nav--prev"
                  onClick={(e) => { e.stopPropagation(); prevStory(); }}
                  aria-label="Назад"
                  title="Назад"
                />
              )}
              {currentStoryIndex < totalStories - 1 && (
                <button
                  type="button"
                  className="rd-stories__nav rd-stories__nav--next"
                  onClick={(e) => { e.stopPropagation(); nextStory(); }}
                  aria-label="Вперёд"
                  title="Вперёд"
                />
              )}
            </div>

            {/* Подвал */}
            <div className="rd-stories__foot">
              <button
                type="button"
                className="rd-stories__cta"
                onClick={handleClose}
              >
                Понятно, спасибо!&nbsp;✨
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Просмотр картинки на весь экран */}
      {fullscreenImage && (
        <div
          className="rd-stories-fullscreen"
          onClick={() => setFullscreenImage(null)}
          role="presentation"
        >
          <img
            src={fullscreenImage.src}
            alt={fullscreenImage.alt}
            className="rd-stories-fullscreen__img"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="rd-stories-fullscreen__close"
            onClick={() => setFullscreenImage(null)}
            aria-label="Закрыть просмотр"
            title="Закрыть"
          />
        </div>
      )}
    </>,
    document.body
  );
};

export default NotificationStories;
